import React, { useState, useEffect, useCallback, createRef } from 'react';
import { Box, Input, Select, useBreakpointIndex } from '@apia/theme';
import { getLabel, noNaN } from '../../utils/util';
import IconButton from './buttons/IconButton';
import SimpleButton from './buttons/SimpleButton';
import { TRootState } from '../../store';
import { useAppSelector } from '../../hooks/hooks';
import useCurrentListId from '../apia/common/lists/hooks/useCurrentListId';
import useTooltip from '../../hooks/useTooltip';
import useIsMaximized from '../apia/common/lists/hooks/useIsMaximized';
import { TId } from './generic/tree/types';
import { getSpecificParent } from '../../utils/dom';
import { useMount } from 'ahooks';

export interface IPagination {
  areAllFiltersApplied?: boolean;

  className?: string;
  currentPage: number | string;
  disabled?: boolean;
  disableReduced?: boolean;
  getAppliedFilters?: (globalState: TRootState) => number;
  /**
   * Si la cantidad de registros se lee desde redux, se puede pasar un selector
   * para leerla.
   */
  getRecordsCount?: (globalState: TRootState) => number;
  hideMaximizeButton?: boolean;
  hideRefreshButton?: boolean;
  isLoading?: boolean;
  isPaged?: boolean;
  /**
   * Se usa principalmente en grillas de ejecución para indicar al pagination
   * que debe usar el fieldId de la misma y no el de un listado global.
   */
  listId?: TId;
  /**
   * Si se pasa un método onDeleteFilters, aparece el botón "Eliminar filtros"
   * cerca del refresh
   */
  onDeleteFilters?: () => unknown;
  onPageChange: (page: number) => unknown;
  onRefresh: (page: number) => unknown;
  pageCount: number | string;
  reachedMax?: boolean | undefined;
  hasMore?: boolean | undefined;
  /**
   * Si la cantidad de registros se obtiene desde un lugar distinto de Redux,
   * se puede pasar como number directamente
   */
  recordsCount?: number;
  showMaximizeOnSmallBreakpoints?: boolean;
  variant?: 'primary' | 'secondary' | 'datagrid';
}

const Pagination = ({
  areAllFiltersApplied,
  className,
  currentPage,
  disabled,
  disableReduced,
  getAppliedFilters,
  getRecordsCount,
  hasMore,
  hideMaximizeButton,
  hideRefreshButton,
  isLoading,
  isPaged,
  listId: outerListId,
  onDeleteFilters,
  onPageChange,
  onRefresh,
  pageCount,
  recordsCount: outerRecordsCount,
  reachedMax,
  showMaximizeOnSmallBreakpoints,
  variant = 'primary',
}: IPagination) => {
  const numberPageCount = Number(pageCount);
  const breakPoint = useBreakpointIndex();
  const numberCurrentPage = Number(currentPage);
  const reduxRecordsCount = useAppSelector(getRecordsCount ?? (() => -1));
  const recordsCount = outerRecordsCount ?? reduxRecordsCount;
  const isPerforming = useAppSelector((global) =>
    global.buttonsSlice.performingActions.includes('disableTable'),
  );
  const [internCurrentPage, setCurrentPage] = useState<number>(
    numberCurrentPage ?? 1,
  );

  const [lastEmittedPage, setLastEmittedPage] = useState<number>(
    numberCurrentPage ?? 1,
  );

  const appliedFilters = useAppSelector(getAppliedFilters ?? (() => 0));
  const [hasNoValue, setHasNoValue] = useState<boolean>(false);

  useEffect(() => {
    setCurrentPage(numberCurrentPage);
  }, [numberCurrentPage]);
  const setCurrentAndDispatch = useCallback(
    (page: number) => {
      setCurrentPage(page);
      onPageChange(page);
      setLastEmittedPage(page);
    },
    [onPageChange],
  );

  const InputRef = createRef<HTMLInputElement>();
  const listId = useCurrentListId();
  const { isMaximized, toggleIsMaximized } = useIsMaximized(
    outerListId ?? listId,
  );
  const handleMaximize = React.useCallback(() => {
    localStorage.setItem(
      `${outerListId ?? listId}__isMaximized`,
      String(!isMaximized),
    );
    toggleIsMaximized();
  }, [isMaximized, listId, outerListId, toggleIsMaximized]);

  useMount(() => {
    const isMaximized =
      localStorage.getItem(`${outerListId ?? listId}__isMaximized`) === 'true';
    toggleIsMaximized(isMaximized);
  });

  const isEveryFilterApplied = useAppSelector(
    (global) => global.querySlice.queries[listId]?.areAllFiltersApplied,
  );

  const options = [];
  for (let i = 1; i <= numberPageCount; i++) {
    options.push(i);
  }

  const [infoButtonRef, setInfoButtonRef] = React.useState<HTMLElement | null>(
    null,
  );

  const recordsCountLabel = `${window.GNR_TOT_RECORDS}: ${recordsCount}`;
  const { toggle } = useTooltip(
    React.useMemo(
      () => ({
        text:
          breakPoint <= 3
            ? `${recordsCountLabel}.    
    ${
      !reachedMax && !hasMore
        ? ''
        : (reachedMax && window.GNR_TOT_RECORDS_REACHED) ||
          (hasMore && window.QUERY_MORE_DATA)
    }`
            : `${
                !reachedMax && !hasMore
                  ? ''
                  : (reachedMax && window.GNR_TOT_RECORDS_REACHED) ||
                    (hasMore && window.QUERY_MORE_DATA)
              }`,

        attachToElement: infoButtonRef,
        closeOnClickOut: true,
        closeOnClickOutChecker(element) {
          return !getSpecificParent(element, (current) =>
            current.classList.contains('pagination__information'),
          );
        },
      }),
      [breakPoint, hasMore, infoButtonRef, reachedMax, recordsCountLabel],
    ),
  );

  return (
    <Box
      as="nav"
      {...window.getVariant(`layout.common.components.pagination.${variant}`)}
      aria-label={getLabel('lblQryParamPag').text}
      className={`pagination ${
        numberPageCount <= 1 && !disableReduced ? 'onlyRefresh' : ''
      } ${className ?? ''}`}
    >
      <Box className="pagination__leftToolbar">
        {!hideMaximizeButton &&
          (showMaximizeOnSmallBreakpoints || breakPoint > 4) && (
            <IconButton
              {...window.getVariant('icon')}
              className="pagination__fullScreenButton"
              onClick={handleMaximize}
              icon={!isMaximized ? 'Maximize' : 'Minimize'}
              title={!isMaximized ? window.LBL_MAXIMIZE : window.LBL_MINIMIZE}
              aria-label={
                !isMaximized ? window.LBL_MAXIMIZE : window.LBL_MINIMIZE
              }
              iconSize="iconMd"
            />
          )}
        {(((reachedMax || hasMore) && breakPoint > 3) || breakPoint <= 3) &&
        className !== 'grid__pagination' ? (
          <IconButton
            {...window.getVariant('icon-primary')}
            icon="Info"
            className={`pagination__information ${
              reachedMax || hasMore ? 'pagination__alertInfo' : ''
            }`}
            onClick={toggle}
            ref={setInfoButtonRef}
            iconSize="iconMd"
            aria-label={window.BTN_FILE_INFO_TOOLTIP}
            title={window.BTN_FILE_INFO_TOOLTIP}
          />
        ) : (
          ''
        )}
        {breakPoint > 3 && (
          <Box
            as="span"
            className="recordsCounter pagination__deleteLabels"
            title={recordsCountLabel}
          >
            {recordsCount !== -1 &&
              `${window.GNR_TOT_RECORDS}: ${recordsCount} `}
          </Box>
        )}
      </Box>

      <Box className="pagination__controls">
        {(numberPageCount > 0 || disableReduced) && isPaged !== false && (
          <>
            {breakPoint > 2 && (
              <IconButton
                {...window.getVariant('icon-primary')}
                disabled={
                  disabled ||
                  internCurrentPage === 1 ||
                  isLoading ||
                  isPerforming
                }
                aria-label={window.NAV_FIRST_PAGE}
                type="button"
                onClick={() => {
                  setCurrentAndDispatch(1);
                }}
                icon="First"
                iconSize="iconSm"
              />
            )}

            <IconButton
              {...window.getVariant('icon-primary')}
              disabled={
                disabled || internCurrentPage <= 1 || isLoading || isPerforming
              }
              aria-label={window.NAV_PREVIOUS_PAGE}
              type="button"
              onClick={() => {
                setCurrentAndDispatch(internCurrentPage - 1);
              }}
              icon="Previous"
              iconSize="iconSm"
            />
            {breakPoint > 2 && (
              <Box className="input">
                <Input
                  className="pagination__controls__input"
                  aria-label={window.NAV_INPUT_LABEL}
                  title={`${window.PAGE}: ${internCurrentPage} `}
                  ref={InputRef}
                  value={hasNoValue ? '' : internCurrentPage}
                  onKeyDown={(ev) => {
                    const regex = /([0-9])|(Backspace)|(Tab)/;
                    if (!ev.key.match(regex)) {
                      ev.stopPropagation();
                      ev.preventDefault();
                    }
                    if (ev.key === 'Enter') {
                      ev.stopPropagation();
                      ev.preventDefault();
                      setCurrentAndDispatch(internCurrentPage);
                    }
                  }}
                  onBlur={() => {
                    if (lastEmittedPage !== internCurrentPage) {
                      setCurrentAndDispatch(internCurrentPage);
                    }
                  }}
                  onFocus={(ev) => {
                    ev.currentTarget.select();
                  }}
                  onChange={(ev) => {
                    setHasNoValue(ev.target.value === '');
                    setCurrentPage(
                      Math.max(
                        1,
                        Math.min(
                          noNaN(Number(ev.currentTarget.value)),
                          pageCount as number,
                        ),
                      ),
                    );
                  }}
                  disabled={disabled || isLoading || isPerforming}
                />{' '}
                /{' '}
                <Box
                  as="span"
                  title={
                    recordsCount !== -1
                      ? `${window.GNR_TOT_RECORDS} ${recordsCount}`
                      : window.NAV_INPUT_LABEL
                  }
                >
                  {pageCount}
                </Box>
              </Box>
            )}
            {breakPoint <= 2 && (
              <Select
                className="pagination__selectPage"
                // menuplacement="top"
                value={internCurrentPage}
                onChange={(e) => {
                  setCurrentAndDispatch(Number(e.target.value));
                }}
              >
                <option disabled>Total: {numberPageCount}</option>
                {options.map((current) => {
                  return <option key={current}>{current}</option>;
                })}
              </Select>
            )}
            <IconButton
              {...window.getVariant('icon-primary')}
              disabled={
                disabled ||
                internCurrentPage >= numberPageCount ||
                isLoading ||
                isPerforming
              }
              aria-label={window.NAV_NEXT_PAGE}
              type="button"
              onClick={() => {
                setCurrentAndDispatch(internCurrentPage + 1);
              }}
              icon="Next"
              iconSize="iconSm"
            />

            {breakPoint > 2 && (
              <IconButton
                {...window.getVariant('icon-primary')}
                type="button"
                aria-label={window.NAV_LAST_PAGE}
                disabled={
                  disabled ||
                  internCurrentPage >= numberPageCount ||
                  isLoading ||
                  isPerforming
                }
                onClick={() => {
                  setCurrentAndDispatch(numberPageCount);
                }}
                icon="Last"
                iconSize="iconSm"
              />
            )}
          </>
        )}
      </Box>
      <Box className="pagination__rightToolbar">
        {(!hideRefreshButton || onDeleteFilters) && (
          <>
            {onDeleteFilters && (
              <SimpleButton
                disabled={isPerforming}
                className="pagination__deleteFiltersButton"
                data-id="delete-filters"
                onClick={onDeleteFilters}
              >
                {breakPoint > 3 && (
                  <Box as="span" className="pagination__deleteLabels">
                    {window.BTN_DELETE_FILTERS}
                  </Box>
                )}
                <Box
                  as="span"
                  className={`pagination__appliedFiltersLabel ${
                    areAllFiltersApplied || isEveryFilterApplied
                      ? ''
                      : 'withFilters'
                  }`}
                >
                  {appliedFilters}
                </Box>
              </SimpleButton>
            )}
            {!hideRefreshButton && (
              <IconButton
                aria-label={window.NAV_REFRESH}
                onClick={() => onRefresh(numberCurrentPage)}
                icon="Refresh"
                iconSize="iconMd"
                disabled={disabled || isLoading}
                {...window.getVariant('icon-primary')}
                isLoading={isPerforming}
                className="pagiantion__refreshIcon"
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Pagination;
